import { RJSFSchema } from "@rjsf/utils";

import { GMWEventRegistrationType, GMWEventTask } from "@nens/brostar/uploadapi-client";
import { SchemaForFormData } from "@nens/shared-utils";

import { FormType } from "@/types/form-types";

import { getGmwEventBaseSchema } from "./gmw-event-base-schema";
import { gmwGroundLevelSchema } from "./gmw-event-ground-level";
import { gmwGroundLevelMeasuringSchema } from "./gmw-event-ground-level-measuring";
import { gmwInsertionSchema } from "./gmw-event-insertion";
import { gmwLengtheningSchema } from "./gmw-event-lengthening";
import { gmwMaintainerSchema } from "./gmw-event-maintainer";
import { gmwOwnerSchema } from "./gmw-event-owner";
import { gmwPositionSchema } from "./gmw-event-positions";
import { gmwPositionsMeasuringSchema } from "./gmw-event-positions-measuring";
import { gmwRemovalSchema } from "./gmw-event-removal";
import { gmwShiftSchema } from "./gmw-event-shift";
import { gmwShorteningSchema } from "./gmw-event-shortening";
import { gmwSubsequentAdditionalSurveySchema } from "./gmw-event-survey";
import { gmwTubeStatusSchema } from "./gmw-event-tube-status";
import { gmwWellHeadProtectorSchema } from "./gmw-event-well-head-protector";

/**
 * Extracts the sourcedocumentData type for a specific registration type.
 * Use this as the return type for schema functions to ensure type safety.
 */
export type SourcedocumentDataSchemaFor<K extends GMWEventRegistrationType> = SchemaForFormData<
  GMWEventTask<K>["sourcedocumentData"]
>;

/**
 * Type-safe registry mapping each GMWEventRegistrationType to its sourcedocumentData schema.
 * Each schema constant has an explicit type using SourcedocumentDataSchemaFor.
 */
const schemaRegistry = {
  GMW_GroundLevel: gmwGroundLevelSchema,
  GMW_GroundLevelMeasuring: gmwGroundLevelMeasuringSchema,
  GMW_Insertion: gmwInsertionSchema,
  GMW_Lengthening: gmwLengtheningSchema,
  GMW_Maintainer: gmwMaintainerSchema,
  GMW_Owner: gmwOwnerSchema,
  GMW_Positions: gmwPositionSchema,
  GMW_PositionsMeasuring: gmwPositionsMeasuringSchema,
  GMW_Removal: gmwRemovalSchema,
  GMW_Shift: gmwShiftSchema,
  GMW_Shortening: gmwShorteningSchema,
  GMW_TubeStatus: gmwTubeStatusSchema,
  GMW_WellHeadProtector: gmwWellHeadProtectorSchema,
  GMW_SubsequentAdditionalSurvey: gmwSubsequentAdditionalSurveySchema,
} satisfies Record<GMWEventRegistrationType, SourcedocumentDataSchemaFor<GMWEventRegistrationType>>;

/**
 * Generates a unified schema with if/then/else conditionals for all registration types.
 * Fields will dynamically show/hide based on the selected registrationType.
 * The correctionReason field is conditionally required when editing.
 * @param type - The type of form ("edit" or "upload")
 * @param isEditing - Whether the form is in edit mode (determines if correctionReason is required)
 */
export function gmwEventFormSchema({
  type,
  isEditing,
}: {
  type: FormType | undefined;
  isEditing: boolean;
}): RJSFSchema {
  const baseSchema = getGmwEventBaseSchema({ type, isEditing });

  // Generate if/then/else conditionals for each registration type
  const allOf = (Object.keys(schemaRegistry) as GMWEventRegistrationType[]).map(
    (registrationType) => {
      const sourcedocumentDataSchema = schemaRegistry[registrationType];

      return {
        if: {
          properties: {
            registrationType: { const: registrationType },
          },
        },
        then: {
          properties: {
            sourcedocumentData: sourcedocumentDataSchema,
          },
        },
      };
    }
  );

  return {
    ...baseSchema,
    allOf,
  } as RJSFSchema;
}
